import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, Loader2 } from 'lucide-react';
import { SubscriptionService } from '@/services/subscriptionService';
import { SubscriptionPriceService } from '@/services/subscriptionPriceService';
import { toast } from 'sonner';
import { useQuery, useMutation } from '@tanstack/react-query';
import { PlanCard } from '@/components/pricing/shared/PlanCard';
import { matchDatabasePlanWithSharedData, getEnhancedFeaturesForDatabasePlan, PricingPlan } from '@/data/pricingPlans';

interface SubscriptionPlansProps {
  organizationId: string;
  onSubscribed?: () => void;
  currentPlanId?: string;
}

export const SubscriptionPlans: React.FC<SubscriptionPlansProps> = ({ 
  organizationId,
  onSubscribed,
  currentPlanId 
}) => {
  const [selectedPlanSlug, setSelectedPlanSlug] = useState<string | null>(null);
  
  // Fetch subscription plans
  const { data: plans, isLoading: isLoadingPlans } = useQuery({
    queryKey: ['subscriptionPlans', organizationId],
    queryFn: () => SubscriptionPriceService.getPricesForOrganization(organizationId)
  });
  
  // Create checkout session mutation
  const checkoutMutation = useMutation({
    mutationFn: (planSlug: string) => SubscriptionService.createCheckoutSession(
      organizationId,
      planSlug,
      `${window.location.origin}/admin/subscription-center?success=true`,
      `${window.location.origin}/admin/subscription-center`
    ),
    onSuccess: (data) => {
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error('Failed to create checkout session');
      }
    },
    onError: (error) => {
      toast.error('Failed to start checkout process');
      console.error('Checkout error:', error);
    }
  });

  // Payment Links mapping (for authenticated users in subscription center)
  const PAYMENT_LINKS = {
    'essential_monthly': 'https://buy.stripe.com/aFa7sM7YNewSgHe7Ywa3u04', // No trial for existing users
    'hivetivity-advanced': 'https://buy.stripe.com/cNi3cw3IxcoKcqYemUa3u01',
    // Enterprise handled separately - redirects to /contact
  };

  // Handle selecting a plan
  const handleSelectPlan = (planOrSlug: string | PricingPlan) => {
    const planSlug = typeof planOrSlug === 'string' ? planOrSlug : (planOrSlug.id || '');
    setSelectedPlanSlug(planSlug);
    
    // Special handling for Enterprise - redirect to contact page
    if (planSlug === 'enterprise_monthly') {
      window.location.href = '/contact';
      return;
    }
    
    // Use direct Payment Link if available
    const paymentLink = PAYMENT_LINKS[planSlug as keyof typeof PAYMENT_LINKS];
    if (paymentLink) {
      window.location.href = paymentLink;
    } else {
      // Fallback to old checkout session method for plans without Payment Links
      checkoutMutation.mutate(planSlug);
    }
  };

  if (isLoadingPlans) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!plans || plans.length === 0) {
    return (
      <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-md">
        <p className="text-yellow-800">No subscription plans are currently available. Please try again later.</p>
      </div>
    );
  }

  // Sort plans by price
  const sortedPlans = [...plans].sort((a, b) => a.amount_cents - b.amount_cents);

  // Find the "recommended" plan (middle option if 3 plans, otherwise highest)
  const recommendedPlan = sortedPlans.length === 3 
    ? sortedPlans[1] 
    : sortedPlans[sortedPlans.length - 1];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6 items-stretch">
      {sortedPlans.map((plan) => {
        const isRecommended = plan.id === recommendedPlan.id;
        const isSelected = plan.slug === selectedPlanSlug;
        const isCurrentPlan = currentPlanId && (plan.id === currentPlanId || plan.slug === currentPlanId || plan.stripe_price_id === currentPlanId);
        
        // Try to match with shared pricing data
        const sharedPlan = matchDatabasePlanWithSharedData(plan);
        const enhancedFeatures = getEnhancedFeaturesForDatabasePlan(plan);
        
        // If we have a shared plan match, use the enhanced PlanCard component
        if (sharedPlan) {
          return (
            <PlanCard
              key={plan.slug || plan.id}
              plan={{
                ...sharedPlan,
                // Override with database-specific values
                price: Math.round(plan.amount_cents / 100),
                period: plan.billing_period === 'monthly' ? 'month' : plan.billing_period === 'yearly' ? 'year' : 'term'
              }}
              variant="admin"
              isCurrentPlan={isCurrentPlan}
              isRecommended={isRecommended}
              isLoading={checkoutMutation.isPending && isSelected}
              onSelectPlan={(pricingPlan) => handleSelectPlan(plan.slug || plan.id)}
              customFeatures={enhancedFeatures}
            />
          );
        }
        
        // Fallback to original card for unmatched plans
        return (
          <div key={plan.slug || plan.id} className="relative">
            {isRecommended && (
              <div className="absolute -top-3 left-0 right-0 z-10">
                <div className="bg-primary text-primary-foreground py-1 px-4 text-center text-xs font-medium tracking-wider rounded-t-lg mx-auto w-fit">
                  RECOMMENDED
                </div>
              </div>
            )}
            <Card 
              className={`flex flex-col h-full ${isRecommended ? 'border-primary shadow-sm' : 'border-gray-200'} ${isCurrentPlan ? 'bg-gray-50' : ''}`}
            >
              <CardHeader className="pb-4 text-center">
              <div className="flex items-center justify-between">
                <CardTitle className="text-xl font-semibold text-center w-full">{plan.name}</CardTitle>
                {isCurrentPlan && (
                  <span className="text-xs font-medium px-2 py-1 bg-primary/10 text-primary rounded absolute top-2 right-2">
                    Current Plan
                  </span>
                )}
              </div>
              <CardDescription className="text-sm text-muted-foreground mt-1 text-center">{plan.description}</CardDescription>
            </CardHeader>
            <CardContent className="pt-0 flex-1 flex flex-col">
              <div className="mb-6 text-center">
                <p className="text-4xl font-bold">
                  ${(plan.amount_cents / 100).toFixed(0)}
                  <span className="text-base text-muted-foreground font-normal">
                    /{plan.billing_period === 'monthly' ? 'mo' : plan.billing_period === 'yearly' ? 'yr' : 'term'}
                  </span>
                </p>
              </div>
              
              {/* Feature list */}
              <div className="mb-6 flex-1">
                <div className="space-y-2 text-left">
                  {enhancedFeatures.map((feature, index) => (
                    <div key={index} className="flex items-start gap-3 text-left">
                      <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-gray-700 leading-relaxed text-left">{feature}</p>
                    </div>
                  ))}
                </div>
              </div>

              <Button 
                className="w-full transform transition-all duration-300 hover:scale-[1.02] hover:shadow-lg active:scale-[0.98] rounded-xl" 
                onClick={() => handleSelectPlan(plan.slug || plan.id)}
                disabled={checkoutMutation.isPending || isCurrentPlan}
                variant={isCurrentPlan ? "secondary" : isRecommended ? "default" : "outline"}
              >
                {checkoutMutation.isPending && isSelected ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : isCurrentPlan ? (
                  'Your Current Plan'
                ) : (
                  `Choose ${plan.name}`
                )}
              </Button>
            </CardContent>
          </Card>
          </div>
        );
      })}
    </div>
  );
};